/**
 * Where you're signed in (2026-09-28, module 1.5.0) — on /security, above "Sign out everywhere".
 *
 * Lists the person's OWN active sign-ins on this site (`mySessions`): device words, the IP of the
 * last request, when it signed in and when it was last active, most recently active first. The
 * one making this request is marked "This device". Each row has a Sign out button: an in-page
 * confirm (kit `useAuthConfirm`), then `revokeMySession`, and only the SERVER's answer is shown —
 * "Signed out Safari on iPhone at 9:14 pm." Signing out the current row signs this device out and
 * goes to /login.
 *
 * Only sign-ins made after this shipped carry a record; older ones (and some emailed-link sign-ins
 * from other modules) aren't listed — the card says so, and "Sign out everywhere" still ends them.
 * No step-up on purpose (same reasoning as sign out everywhere: it only takes access away).
 */
import React from "react";
import { Box, Button, Flex, Heading, HStack, Icon, Skeleton, Stack, Text } from "@chakra-ui/react";
import { FiCheckCircle, FiLogOut, FiMonitor, FiSmartphone, FiTablet, FiHelpCircle } from "react-icons/fi";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import type { Mutation, MutationRevokeMySessionArgs, Query, SignInSessionView } from "../../../generated/graphql";
import { MY_SESSIONS, REVOKE_MY_SESSION } from "../queries";
import { errorWords, signedOutAtWords } from "../utils/assurance";
import { lastActiveWords, SESSIONS_LIST_CAP, signInMethodWords, whereWords } from "../utils/sessions";
import { AuthError, AuthStatusPill, AuthSurface, AuthTip, alpha, blurClick, useAuthConfirm, useAuthTheme } from "./ui";

export const WHERE_SIGNED_IN_ORDER = "Most recently active first.";

export const signOutRowTip = (label: string, isCurrent: boolean) =>
  isCurrent
    ? `Sign out on this device.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. This sign-in stops working straight away\n  3. You go to the sign-in page\n\nThis changes your account. Your other devices stay signed in.`
    : `Sign out ${label}.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. That device is signed out straight away and has to sign in again\n  3. This device stays signed in\n\nThis changes your account.`;

const DEVICE_ICON: Record<string, React.ElementType> = { desktop: FiMonitor, mobile: FiSmartphone, tablet: FiTablet };

export const WhereSignedInCard: React.FC = () => {
  const t = useAuthTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useAuthConfirm();
  const { data, loading, error, refetch } = useQuery<Pick<Query, "mySessions">>(MY_SESSIONS, { fetchPolicy: "cache-and-network", notifyOnNetworkStatusChange: false });
  const [revokeMut] = useMutation<Pick<Mutation, "revokeMySession">, MutationRevokeMySessionArgs>(REVOKE_MY_SESSION);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [confirmed, setConfirmed] = React.useState<{ label: string; at: string } | null>(null);
  const [failure, setFailure] = React.useState<string | null>(null);

  // The confirm dialog hands focus back to the row's button, and a focused control re-opens its
  // tooltip — let go of focus once the dialog is done (Polish rule 47), as SignOutEverywhereCard does.
  const releaseFocus = () => window.setTimeout(() => (document.activeElement as HTMLElement | null)?.blur?.(), 60);

  const sessions: SignInSessionView[] = data?.mySessions ?? [];
  const hasCurrent = sessions.some((s) => s.isCurrent);

  const signOut = async (s: SignInSessionView) => {
    const ok = await confirm({
      title: s.isCurrent ? "Sign out on this device?" : `Sign out ${s.deviceLabel}?`,
      body: s.isCurrent
        ? "This device is signed out straight away and you go to the sign-in page. Your other devices stay signed in."
        : `${s.deviceLabel} (${whereWords(s)}) is signed out straight away and has to sign in again. This device stays signed in.`,
      confirmLabel: s.isCurrent ? "Sign out here" : "Sign out that device",
      tone: "warning",
    });
    releaseFocus();
    if (!ok) return;
    setBusyId(s.id);
    setFailure(null);
    setConfirmed(null);
    try {
      const res = await revokeMut({ variables: { id: s.id } });
      const out = res.data?.revokeMySession;
      if (!out?.revokedAt) throw new Error("The server didn't confirm it. That device may still be signed in — please try again.");
      if (out.wasThisDevice) {
        auth.logout();
        navigate("/login");
        return;
      }
      setConfirmed({ label: s.deviceLabel, at: out.revokedAt });
      await refetch().catch(() => undefined);
    } catch (err) {
      setFailure(errorWords(err, "Couldn't sign out that device. Please try again."));
      await refetch().catch(() => undefined);
    } finally {
      setBusyId(null);
      releaseFocus();
    }
  };

  return (
    <AuthSurface data-testid="where-signed-in-card">
      <HStack spacing={3} mb={1} align="center">
        <Box as={FiMonitor} color={t.text} aria-hidden />
        <Heading size="sm" color={t.text}>Where you're signed in</Heading>
      </HStack>
      <Text fontSize="md" color={t.muted} mb={4}>
        The phones, tablets and computers signed in to your account on this site. If you don't recognise one, sign it out.
      </Text>

      <Stack spacing={3}>
        {confirmed && (
          <HStack role="status" align="start" spacing={2} p={3} borderRadius="lg" bg={alpha(t.success, 0.1)} borderWidth="1px" borderColor={alpha(t.success, 0.35)} data-testid="where-signed-in-confirmed">
            <Box as={FiCheckCircle} color={t.success} mt="3px" flexShrink={0} aria-hidden />
            <Text fontSize="md" color={t.text}>Signed out {confirmed.label} at {signedOutAtWords(confirmed.at)}. It has to sign in again to get back in.</Text>
          </HStack>
        )}
        {failure && (
          <Text role="alert" fontSize="md" color={t.danger} data-testid="where-signed-in-failure">{failure}</Text>
        )}

        {loading && !data ? (
          <Box role="status" aria-label="Checking where you're signed in" data-testid="where-signed-in-loading">
            <Stack spacing={3}>
              {[0, 1].map((i) => <Skeleton key={i} h="64px" borderRadius="lg" />)}
            </Stack>
            <Text fontSize="md" color={t.faint} mt={2}>Checking where you're signed in…</Text>
          </Box>
        ) : error && !data ? (
          <AuthError title="Couldn't load where you're signed in" error={error} onRetry={() => refetch()} />
        ) : (
          <>
            <Text fontSize="sm" color={t.faint} data-testid="where-signed-in-count">
              {sessions.length === 0
                ? "No sign-ins are listed yet."
                : `${sessions.length} sign-in${sessions.length === 1 ? "" : "s"}. ${WHERE_SIGNED_IN_ORDER}`}
              {sessions.length >= SESSIONS_LIST_CAP ? ` Showing the ${SESSIONS_LIST_CAP} most recently active only.` : ""}
            </Text>
            <Stack as="ul" spacing={3} listStyleType="none" m={0} p={0}>
              {sessions.map((s) => {
                const method = signInMethodWords(s.amr);
                return (
                  <Box
                    as="li"
                    key={s.id}
                    p={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={s.isCurrent ? alpha(t.primary, 0.45) : t.borderStrong}
                    bg={s.isCurrent ? alpha(t.primary, 0.06) : "transparent"}
                    data-testid="where-signed-in-row"
                  >
                    <Flex direction={{ base: "column", sm: "row" }} gap={3} align={{ base: "stretch", sm: "center" }}>
                      <HStack spacing={3} align="start" flex="1" minW={0}>
                        <Flex flexShrink={0} w={10} h={10} borderRadius="lg" bg={alpha(t.primary, 0.12)} align="center" justify="center" aria-hidden>
                          <Icon as={DEVICE_ICON[s.deviceType] || FiHelpCircle} color={t.primary} />
                        </Flex>
                        <Box minW={0}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Text fontSize="md" fontWeight="semibold" color={t.text} wordBreak="break-word">{s.deviceLabel}</Text>
                            {s.isCurrent && <AuthStatusPill kind="device" status="THIS" />}
                          </HStack>
                          <Text fontSize="sm" color={t.muted}>
                            {s.isCurrent ? "Active now" : lastActiveWords(s.lastSeenAt)} · {whereWords(s)}
                          </Text>
                          <Text fontSize="sm" color={t.muted}>
                            Signed in {signedOutAtWords(s.createdAt)}{method ? ` · ${method}` : ""}
                          </Text>
                        </Box>
                      </HStack>
                      <AuthTip label={signOutRowTip(s.deviceLabel, s.isCurrent)}>
                        <Button
                          size="lg"
                          minH="48px"
                          w={{ base: "full", sm: "auto" }}
                          variant="outline"
                          colorScheme="orange"
                          leftIcon={<FiLogOut />}
                          isLoading={busyId === s.id}
                          isDisabled={!!busyId && busyId !== s.id}
                          loadingText="Signing out…"
                          onClick={blurClick(() => void signOut(s))}
                          data-testid="where-signed-in-sign-out"
                          aria-label={s.isCurrent ? "Sign out on this device" : `Sign out ${s.deviceLabel}`}
                        >
                          {s.isCurrent ? "Sign out here" : "Sign out"}
                        </Button>
                      </AuthTip>
                    </Flex>
                  </Box>
                );
              })}
            </Stack>
            <Text fontSize="sm" color={t.faint} data-testid="where-signed-in-older">
              {hasCurrent
                ? "Sign-ins from before 28 September 2026, and some made from emailed or texted links, aren't listed. Sign out everywhere (below) ends those too."
                : "This device signed in before this list existed, so it isn't shown — nor are other sign-ins from before 28 September 2026 or some made from emailed or texted links. Sign out everywhere (below) ends those too."}
            </Text>
          </>
        )}
      </Stack>
      {confirmDialog}
    </AuthSurface>
  );
};

export default WhereSignedInCard;
